import { memo, useEffect, useRef } from 'react'

import { DeleteFive } from '@icon-park/react'

import { ThemeGroupResponse, ToggleThemeGroupRequest } from '@shared/models'

import { Alert, Api, Confirmation } from '@/modules'

import { Divider, Flex, Switch } from '@/components'
import { ColumnType, Table } from '@/components/derived'

import { useRouting } from '@/hooks'
import { getFromToElements } from '@/utils/pagination'
import { useCompanyContext } from '../CompanyDetails.context'
import { TableWrapper } from './ThemeGroups.styles'

export const ThemeGroupsTable = memo(Component)

interface ComponentProps {
  isActive: boolean
  toggleIsActive: () => void
}

function Component({ isActive, toggleIsActive }: ComponentProps) {
  const { currentPage, changeQueryParam } = useRouting()
  const { isLoadingThemeGroups, themeGroups, reloadThemeGroups } = useCompanyContext()
  const prevIsActiveRef = useRef(isActive)
  const isAdjustingPageRef = useRef(false)

  const { sendAlert } = Alert.Hook()
  const { open: openDeactivateConfirmation } = Confirmation.Hook({
    icon: DeleteFive,
    text: 'Ao desativar este grupo temático, todos os temas associados a ele também serão desativados. Esta ação pode ser revertida posteriormente.',
    confirmText: 'Sim! Desativar grupo e temas',
  })

  const { isRunning: isToggling, run: toggleThemeGroup } = Api.Command<void>(
    Api.Methods.PATCH,
    'theme-groups/:id/toggle',
  )

  useEffect(() => {
    const isActiveChanged = prevIsActiveRef.current !== isActive
    prevIsActiveRef.current = isActive

    if (isActiveChanged && currentPage !== '1') {
      isAdjustingPageRef.current = true
      changeQueryParam({
        param: 'pagina',
        value: '1',
      })
      return
    }

    if (isAdjustingPageRef.current) {
      isAdjustingPageRef.current = false
    }

    reloadThemeGroups(currentPage, isActive)
  }, [currentPage, isActive])

  function adjustPageIfNeeded(totalAfterAction: number, pageSize: number = 10): boolean {
    const currentPageNum = Number(currentPage)
    const totalPages = Math.ceil(totalAfterAction / pageSize)

    if (currentPageNum > totalPages && totalPages > 0) {
      isAdjustingPageRef.current = true
      changeQueryParam({
        param: 'pagina',
        value: String(totalPages),
      })
      return true
    }
    return false
  }

  function onToggle(id: string, currentStatus: boolean) {
    const newStatus = !currentStatus

    if (!newStatus) {
      openDeactivateConfirmation(() => performToggle(id, newStatus))
    } else {
      performToggle(id, newStatus)
    }
  }

  function performToggle(id: string, newStatus: boolean) {
    const input: ToggleThemeGroupRequest = { isActive: newStatus }

    toggleThemeGroup<ToggleThemeGroupRequest>({
      binds: [['id', id]],
      input,
      onSuccess: () => {
        sendAlert({
          type: 'success',
          title: newStatus ? 'Grupo ativado' : 'Grupo desativado',
          message:
            newStatus ?
              'Grupo temático ativado com sucesso'
            : 'Grupo temático e seus temas foram desativados com sucesso',
        })

        const willRemoveFromList = (isActive && !newStatus) || (!isActive && newStatus)
        let pageWasAdjusted = false

        if (willRemoveFromList && themeGroups?.total) {
          pageWasAdjusted = adjustPageIfNeeded(themeGroups.total - 1)
        }

        if (!pageWasAdjusted) {
          reloadThemeGroups(currentPage, isActive)
        }
      },
    })
  }

  const columns: ColumnType<ThemeGroupResponse>[] = [
    {
      key: 'name',
      title: 'Nome',
    },
    {
      key: 'status',
      title: 'Status',
      width: 100,
      render: (_, item) => {
        const isDefaultThemeGroup = item.name === 'SEM TEMA'

        return (
          <Switch.Root
            isChecked={item.isActive ?? true}
            toggle={() => {
              if (isDefaultThemeGroup) {
                sendAlert({
                  type: 'error',
                  title: 'Ação não permitida',
                  message: 'O grupo temático padrão não pode ser desativado',
                })
                return
              }

              onToggle(item.id, item.isActive ?? true)
            }}
            disabled={isToggling || isDefaultThemeGroup}
          />
        )
      },
    },
  ]

  return (
    <>
      <Divider.Root>
        <Divider.Title value="Grupos temáticos registrados" />
        <Divider.Spacer />
        <Flex $type="row" $align="center" $gap={12}>
          <Switch.Root label="Apenas ativos" isChecked={isActive} toggle={toggleIsActive} direction="row" />
          <Divider.Subtitle
            value={`${getFromToElements(currentPage, themeGroups?.elements.length ?? 0)}/${themeGroups?.total ?? 0} Entrada(s)`}
          />
        </Flex>
      </Divider.Root>
      <TableWrapper>
        <Table
          isLoading={isLoadingThemeGroups}
          columns={columns}
          data={themeGroups?.elements}
          totalPages={themeGroups?.pages}
        />
      </TableWrapper>
    </>
  )
}
